import axios from 'axios';
import { API_BASE_URL } from './config';

const STORAGE_KEY = 'currentOrganizationId';

class OrganizationService {
  constructor() {
    this.currentOrganization = null;
    this.organizations = [];
    this.listeners = new Set();
  }

  /**
   * Fetch user's organizations
   */
  async fetchOrganizations() {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/organizations/`);
      this.organizations = response.data;

      // If no current organization set, default to first available
      if (!this.currentOrganization && this.organizations.length > 0) {
        this.setCurrentOrganization(this.organizations[0]);
      }

      return this.organizations;
    } catch (error) {
      console.error('Error fetching organizations:', error);
      return [];
    }
  }

  /**
   * Get current organization from memory or localStorage
   */
  getCurrentOrganization() {
    if (this.currentOrganization) {
      return this.currentOrganization;
    }

    // Try to restore from localStorage
    const storedId = localStorage.getItem(STORAGE_KEY);
    if (storedId && this.organizations.length > 0) {
      const org = this.organizations.find(o => o.id === parseInt(storedId));
      if (org) {
        this.currentOrganization = org;
        return org;
      }
    }

    return null;
  }

  /**
   * Alias for getCurrentOrganization (for backward compatibility)
   */
  getOrganization() {
    return this.getCurrentOrganization();
  }

  /**
   * Set current organization and persist to localStorage
   */
  setCurrentOrganization(organization) {
    if (!organization) return;

    this.currentOrganization = organization;
    localStorage.setItem(STORAGE_KEY, organization.id.toString());

    // Notify listeners of organization change
    this._notifyListeners(organization);
  }

  /**
   * Alias for setCurrentOrganization (for backward compatibility)
   */
  setOrganization(organization) {
    this.setCurrentOrganization(organization);
  }

  /**
   * Initialize organization service (call after login)
   */
  async initialize() {
    const orgs = await this.fetchOrganizations();

    // Try to restore previous selection
    const storedId = localStorage.getItem(STORAGE_KEY);
    if (storedId) {
      const stored = orgs.find(o => o.id === parseInt(storedId));
      if (stored) {
        this.setCurrentOrganization(stored);
        return stored;
      }
    }

    // Default to first organization
    if (orgs.length > 0) {
      const defaultOrg = orgs[0];
      this.setCurrentOrganization(defaultOrg);
      return defaultOrg;
    }

    return null;
  }

  /**
   * Add a listener for organization changes
   */
  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of organization change
   */
  _notifyListeners(organization) {
    this.listeners.forEach(cb => {
      try {
        cb(organization);
      } catch (error) {
        console.error('Error in organization listener:', error);
      }
    });
  }

  /**
   * Get resource groups for current organization or all organizations
   * @param {number|null} organizationId - Organization ID, or null to get all resource groups
   */
  async getResourceGroups(organizationId = null) {
    try {
      const params = {};

      // If organizationId is explicitly provided (even if it's the current org), use it
      // If organizationId is null/undefined, don't filter by organization
      if (organizationId !== null && organizationId !== undefined) {
        params.organization = organizationId;
      }

      const response = await axios.get(`${API_BASE_URL}/api/resource-groups/`, {
        params
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching resource groups:', error);
      return [];
    }
  }

  /**
   * Clear organization data (on logout)
   */
  clear() {
    this.currentOrganization = null;
    this.organizations = [];
    localStorage.removeItem(STORAGE_KEY);
    this.listeners.clear();
  }

  /**
   * Check if user has organization-level admin access
   */
  canManageOrganization() {
    if (!this.currentOrganization) return false;
    const role = this.currentOrganization.user_role;
    return role === 'OWNER' || role === 'ADMIN';
  }

  /**
   * Get user's role in current organization
   */
  getUserRole() {
    return this.currentOrganization?.user_role || null;
  }
}

// Export singleton instance
const organizationService = new OrganizationService();
export default organizationService;
